import { useState } from 'react';
import { Megaphone, Send } from 'lucide-react';
import { getErrorMessage } from '@/lib/api';
import { desdeHace } from '@/lib/cn';
import { useAvisosDomiciliarios, useEnviarAvisoDomiciliarios } from '@/lib/queries';
import { AreaTexto, Boton, Campo, Entrada, Modal } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

const MAX = 240;

/**
 * Un mensaje para todos los domiciliarios, a su teléfono.
 *
 * Es para animar: «hoy hay demanda en Laureles, conéctate», «gracias por el
 * fin de semana». Llega como notificación push a todos los verificados y
 * activos, estén o no en turno —de eso se trata—. Corto a propósito: una
 * notificación larga se corta sola en la pantalla del celular.
 */
export function AvisoDomiciliarios() {
  const avisos = useAvisos();
  const enviar = useEnviarAvisoDomiciliarios();
  const historial = useAvisosDomiciliarios();
  const [abierto, setAbierto] = useState(false);
  const [titulo, setTitulo] = useState('Kick Domicilios');
  const [texto, setTexto] = useState('');

  const restantes = MAX - texto.length;
  const listo = texto.trim().length >= 5 && titulo.trim().length >= 3;

  const mandar = (): void => {
    enviar.mutate(
      { title: titulo.trim(), body: texto.trim() },
      {
        onSuccess: (r) => {
          avisos.exito(
            r.recipients === 1
              ? 'Aviso enviado a 1 domiciliario'
              : `Aviso enviado a ${r.recipients} domiciliarios`,
          );
          setTexto('');
        },
        onError: (e) => avisos.error(getErrorMessage(e)),
      },
    );
  };

  return (
    <>
      <Boton
        variante="secundario"
        icono={<Megaphone className="h-4 w-4" />}
        onClick={() => setAbierto(true)}
      >
        Aviso a todos
      </Boton>

      <Modal
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
        titulo="Aviso a todos los domiciliarios"
        descripcion="Les llega como notificación al celular, estén o no en turno."
        ancho="md"
      >
        <div className="max-h-[75vh] space-y-4 overflow-y-auto p-5">
          <Campo etiqueta="Título" ayuda="Es lo que sale en negrita en la notificación.">
            <Entrada value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={60} />
          </Campo>

          <Campo
            etiqueta="Mensaje"
            requerido
            ayuda={`${restantes} caracteres disponibles. Corto y directo: en el celular se lee de un vistazo.`}
          >
            <AreaTexto
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, MAX))}
              rows={4}
              placeholder="Ej. ¡Buenas tardes, equipo! Hay muchos pedidos esperando en Laureles y El Poblado. Conéctate y aprovecha la tarde."
            />
          </Campo>

          <div className="flex justify-end">
            <Boton
              icono={<Send className="h-4 w-4" />}
              cargando={enviar.isPending}
              disabled={!listo}
              onClick={mandar}
            >
              Enviar a todos
            </Boton>
          </div>

          <section>
            <h3 className="text-sm font-bold uppercase tracking-wide text-kick-gray">
              Últimos avisos
            </h3>
            {(historial.data ?? []).length === 0 ? (
              <p className="mt-2 text-sm text-kick-gray">Todavía no se ha enviado ninguno.</p>
            ) : (
              <ul className="mt-2 divide-y divide-kick-gray/10 rounded-kick border border-kick-gray/10">
                {historial.data!.map((a) => (
                  <li key={a.id} className="p-3 text-sm">
                    <p className="font-semibold text-kick-ink">{a.title}</p>
                    <p className="mt-0.5 whitespace-pre-line text-kick-ink">{a.body}</p>
                    <p className="mt-1 text-xs text-kick-gray">
                      {desdeHace(a.createdAt)} · {a.senderName ?? 'administración'} ·{' '}
                      {a.recipients} {a.recipients === 1 ? 'domiciliario' : 'domiciliarios'}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </Modal>
    </>
  );
}
